import { useState, useEffect, useRef } from 'react';
import { api } from '../../../api.js';

/**
 * CopyPopover — anchored popover for bulk-copying selected documents into a
 * folder. Unlike Move, this ADDS an appearance (multi-folder membership): the
 * documents keep their home folder and also show up in the destination.
 *
 * Backend: POST /documents/case/{id}/bulk-folder-copies.
 */
export default function CopyPopover({
  caseId, ids, count, folderPaths = [], anchorRef, onClose, onCompleted,
}) {
  const [dest, setDest] = useState(folderPaths[0] || '__new__');
  const [newFolder, setNewFolder] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [anchorRef, onClose]);

  async function handleCopy() {
    let target = dest === '__new__' ? newFolder.trim() : dest;
    if (!target) {
      setError('Folder path required');
      return;
    }
    if (!target.startsWith('/')) target = '/' + target;

    setBusy(true);
    setError(null);
    try {
      await api.post(`/documents/case/${caseId}/bulk-folder-copies`, {
        document_ids: ids,
        folder_path: target,
      });
      onCompleted?.(`Copied ${count} file${count === 1 ? '' : 's'} to ${target}`);
    } catch (err) {
      setError(err.message || 'Copy failed');
      setBusy(false);
    }
  }

  return (
    <div ref={ref} className="bulk-popover" role="dialog" aria-label="Copy documents">
      <div className="bulk-popover-title">Copy {count} doc{count === 1 ? '' : 's'} into:</div>
      <select
        className="bulk-popover-select"
        value={dest}
        onChange={(e) => setDest(e.target.value)}
        disabled={busy}
      >
        {folderPaths.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
        <option value="__new__">+ New folder…</option>
      </select>
      {dest === '__new__' && (
        <input
          type="text"
          className="bulk-popover-input"
          placeholder="/path/to/folder"
          value={newFolder}
          onChange={(e) => setNewFolder(e.target.value)}
          disabled={busy}
          autoFocus
        />
      )}
      <div className="bulk-popover-hint">
        Copies keep the originals in place — the file appears in both folders.
      </div>
      {error && <div className="bulk-popover-error">{error}</div>}
      <div className="bulk-popover-actions">
        <button type="button" className="btn btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
        <button type="button" className="btn btn-sm btn-primary" onClick={handleCopy} disabled={busy}>
          {busy ? 'Copying…' : 'Copy'}
        </button>
      </div>
    </div>
  );
}
